/**
 * PROM admin-schedule fixtures (cardiometabolic + longevity expansion
 * 2026-05-07).
 *
 * Maps each clinical pathway to a four-step admin schedule. Step ids are
 * stable so AuditEvent / telemetry can correlate downstream signal-feed
 * entries to the originating dispatch. Clinical pathway display names
 * are sourced verbatim from `PATHWAY_DISPLAY_NAMES` in
 * `src/layouts/AppShell.tsx` per Brand-Patch v2 (no `TLP1`-`TLP4` strings
 * surface in any user-facing copy).
 *
 * The longevity profile is intentionally modeled as a "profile" not a
 * pathway: it has no FHIR PlanDefinition binding yet and is layered on
 * top of the active pathway when the AdminSchedulePanel toggle is
 * enabled. This preserves the schedule semantics for pathway-bound
 * patients while still exercising the longevity instrument set.
 *
 * HC1: byte-stable - no clocks, no randomness.
 * HC2: tokens-only - data only, styling lives in the panel.
 * HC5: zero em / en dashes.
 */
import type { Pathway } from '../../data/synthetic-patients/types';
import type { PromId } from './prom-definitions';

/** A single step in a four-step PROM admin schedule. */
export interface PromScheduleStep {
  /** Stable id (scopes the AuditEvent + telemetry correlation). */
  id: string;
  /** Step ordinal (1 to 4). */
  ordinal: number;
  /** Status of the step: complete or scheduled. */
  status: 'complete' | 'scheduled';
  /** Status label rendered verbatim in the badge. */
  statusLabel: string;
  /** Date label rendered under the ordinal. */
  dateLabel: string;
  /** PROM ids dispatched in this step. */
  instruments: ReadonlyArray<PromId>;
}

/**
 * Four-step admin schedule for a single pathway. The header label is
 * the user-facing pathway display name (Brand-Patch clinical name).
 */
export interface PromScheduleSet {
  pathway: Pathway;
  /** Verbatim user-facing pathway label. */
  pathwayDisplay: string;
  /** Four steps in ascending ordinal order. */
  steps: ReadonlyArray<PromScheduleStep>;
}

/**
 * Cardiometabolic Pathway four-step schedule. Step 1 ports the existing
 * Sprint 4.1 PathwayView fixtures (PHQ-9 + MSQ + SF-12) so the metabolic
 * dashboard sub-tab and the dedicated PROMs tab agree on the canonical
 * step set.
 */
const CARDIOMETABOLIC_SCHEDULE: PromScheduleSet = {
  pathway: 'cardiometabolic-pathway',
  pathwayDisplay: 'Cardiometabolic Pathway',
  steps: [
    {
      id: 'cardiometabolic-step-1',
      ordinal: 1,
      status: 'complete',
      statusLabel: 'Complete',
      dateLabel: 'Mar 22',
      instruments: ['phq-9', 'gad-7', 'msq-baseline', 'sf-12'],
    },
    {
      id: 'cardiometabolic-step-2',
      ordinal: 2,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'Apr 19',
      instruments: ['gsrs', 'iwqol-lite', 'dqol'],
    },
    {
      id: 'cardiometabolic-step-3',
      ordinal: 3,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'May 3',
      instruments: ['phq-9', 'gad-7', 'msq-baseline', 'sf-12'],
    },
    {
      id: 'cardiometabolic-step-4',
      ordinal: 4,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'Jul 19',
      instruments: [
        'phq-9',
        'gad-7',
        'msq-baseline',
        'sf-12',
        'gsrs',
        'iwqol-lite',
        'dqol',
        'eq-5d-5l',
        'koos-12',
      ],
    },
  ],
};

/**
 * HRT Pathway four-step schedule. Centers on MSQ + GAD-7 + WHO-5 because
 * the canonical HRT cohort (Fatima Al-Zaabi) carries menopause-driven
 * cardiometabolic risk plus mood symptomatology.
 */
const HRT_SCHEDULE: PromScheduleSet = {
  pathway: 'hrt-pathway',
  pathwayDisplay: 'HRT Pathway',
  steps: [
    {
      id: 'hrt-step-1',
      ordinal: 1,
      status: 'complete',
      statusLabel: 'Complete',
      dateLabel: 'Mar 18',
      instruments: ['msq-baseline', 'phq-9', 'gad-7'],
    },
    {
      id: 'hrt-step-2',
      ordinal: 2,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'Apr 15',
      instruments: ['msq-baseline', 'sf-12', 'who-5'],
    },
    {
      id: 'hrt-step-3',
      ordinal: 3,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'May 6',
      instruments: ['msq-baseline', 'phq-9', 'gad-7'],
    },
    {
      id: 'hrt-step-4',
      ordinal: 4,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'Jul 22',
      instruments: ['msq-baseline', 'phq-9', 'gad-7', 'sf-12', 'who-5', 'eq-5d-5l'],
    },
  ],
};

/**
 * Metabolic Syndrome Pathway four-step schedule. Centers on the
 * cardiometabolic + GLP-1 side-effect set (GSRS + IWQOL-Lite + DQOL).
 */
const METABOLIC_SYNDROME_SCHEDULE: PromScheduleSet = {
  pathway: 'metabolic-syndrome-pathway',
  pathwayDisplay: 'Metabolic Syndrome Pathway',
  steps: [
    {
      id: 'metabolic-syndrome-step-1',
      ordinal: 1,
      status: 'complete',
      statusLabel: 'Complete',
      dateLabel: 'Mar 25',
      instruments: ['phq-9', 'sf-12', 'iwqol-lite'],
    },
    {
      id: 'metabolic-syndrome-step-2',
      ordinal: 2,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'Apr 22',
      instruments: ['gsrs', 'dqol', 'iwqol-lite'],
    },
    {
      id: 'metabolic-syndrome-step-3',
      ordinal: 3,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'May 13',
      instruments: ['phq-9', 'sf-12', 'iwqol-lite'],
    },
    {
      id: 'metabolic-syndrome-step-4',
      ordinal: 4,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'Aug 3',
      instruments: [
        'phq-9',
        'sf-12',
        'iwqol-lite',
        'gsrs',
        'dqol',
        'eq-5d-5l',
        'koos-12',
      ],
    },
  ],
};

/**
 * CVD Prevention Pathway four-step schedule. Centers on EQ-5D + SF-12
 * for HRQOL plus PHQ-9 + GAD-7 for affect surveillance.
 */
const CVD_PREVENTION_SCHEDULE: PromScheduleSet = {
  pathway: 'cvd-prevention-pathway',
  pathwayDisplay: 'CVD Prevention Pathway',
  steps: [
    {
      id: 'cvd-prevention-step-1',
      ordinal: 1,
      status: 'complete',
      statusLabel: 'Complete',
      dateLabel: 'Mar 28',
      instruments: ['phq-9', 'gad-7', 'sf-12'],
    },
    {
      id: 'cvd-prevention-step-2',
      ordinal: 2,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'Apr 25',
      instruments: ['eq-5d-5l', 'sf-12', 'koos-12'],
    },
    {
      id: 'cvd-prevention-step-3',
      ordinal: 3,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'May 23',
      instruments: ['phq-9', 'gad-7', 'sf-12'],
    },
    {
      id: 'cvd-prevention-step-4',
      ordinal: 4,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'Aug 11',
      instruments: ['phq-9', 'gad-7', 'sf-12', 'eq-5d-5l', 'koos-12', 'iwqol-lite'],
    },
  ],
};

/** Per-pathway four-step admin schedules in canonical pathway order. */
export const PROM_SCHEDULES_BY_PATHWAY: Readonly<Record<Pathway, PromScheduleSet>> = {
  'cardiometabolic-pathway': CARDIOMETABOLIC_SCHEDULE,
  'hrt-pathway': HRT_SCHEDULE,
  'metabolic-syndrome-pathway': METABOLIC_SYNDROME_SCHEDULE,
  'cvd-prevention-pathway': CVD_PREVENTION_SCHEDULE,
};

/**
 * Longevity Profile schedule. Modeled as a profile, not a pathway: there
 * is no FHIR PlanDefinition binding, and the schedule layers on top of
 * the active pathway when toggled on. Uses the four PROMIS-aligned
 * longevity instruments plus EQ-5D-5L as the cross-cutting HRQOL anchor.
 */
export const LONGEVITY_PROFILE_SCHEDULE: PromScheduleSet = {
  // Reuse the cardiometabolic enum value for type safety; the pathway
  // field is not surfaced anywhere when this profile is rendered.
  pathway: 'cardiometabolic-pathway',
  pathwayDisplay: 'Longevity Profile',
  steps: [
    {
      id: 'longevity-step-1',
      ordinal: 1,
      status: 'complete',
      statusLabel: 'Complete',
      dateLabel: 'Mar 22',
      instruments: ['promis-29', 'who-5', 'eq-5d-5l'],
    },
    {
      id: 'longevity-step-2',
      ordinal: 2,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'Apr 19',
      instruments: ['efi', 'healthy-aging-index'],
    },
    {
      id: 'longevity-step-3',
      ordinal: 3,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'May 17',
      instruments: ['promis-29', 'who-5'],
    },
    {
      id: 'longevity-step-4',
      ordinal: 4,
      status: 'scheduled',
      statusLabel: 'Scheduled',
      dateLabel: 'Sep 14',
      instruments: ['promis-29', 'who-5', 'eq-5d-5l', 'efi', 'healthy-aging-index'],
    },
  ],
};
